import { useAppSelector } from './useAppStore';
import { useGetTenantSubscriptionQuery } from '../store/api/tenantSubscriptionApi';
import { esPlanGratis } from '../utils/planGratis';

/**
 * Modo SOLO LECTURA: un tenant booster con la suscripción suspendida/cancelada puede ver
 * su data pero no crear/editar. El backend lo bloquea (402); esto deshabilita los botones
 * de creación en la UI para que el usuario no lo intente. Imperium (native) nunca es read-only.
 */
export function useReadOnly(): { readOnly: boolean; reason: string } {
  const sub = useAppSelector((s) => s.auth.user?.subscription);
  // Un plan gratis nunca cae en solo lectura: no hay pago que regularizar.
  const readOnly = !!sub && !sub.native && !esPlanGratis(sub) && (sub.status === 'suspended' || sub.status === 'canceled');
  return {
    readOnly,
    reason: 'Tu suscripción está suspendida — el CRM está en solo lectura. Regularizá el pago para crear o editar.',
  };
}

/**
 * ¿Gustavo está bloqueado para este tenant? Por suscripción suspendida o por haber agotado
 * el pool mensual de Gustavo del plan. Native nunca se bloquea. Se usa para deshabilitar el
 * input del chat (mejor que mandar y chocar con el 402).
 */
export function useGustavoBlocked(): { blocked: boolean; reason: string } {
  const authSub = useAppSelector((s) => s.auth.user?.subscription);
  const isDemo = useAppSelector((s) => s.auth.user?.isDemo);
  const role = useAppSelector((s) => s.auth.user?.role);
  const native = !!authSub?.native;

  // El endpoint /tenant-subscription/me está restringido a los DUEÑOS del plan
  // (tenant_admin/office_admin/tenant_agent). Un agente empleado no tiene acceso → 403.
  // Para esos roles NO pedimos el dato en vivo: usamos el del login (authSub), que
  // alcanza para el bloqueo. Evita el 403 de fondo en el dashboard.
  const canReadSub = role === 'tenant_admin' || role === 'office_admin' || role === 'tenant_agent';

  // Datos EN VIVO de la suscripción (tag `Subscription`): se refrescan al invalidar tras cada
  // mensaje de Gustavo / recarga, así el bloqueo aparece o se libera SIN re-loguear.
  // El estado de auth (cargado al login) es solo el fallback del primer render.
  const { data: live } = useGetTenantSubscriptionQuery(undefined, { skip: !authSub || native || !canReadSub });

  if (!authSub || native || esPlanGratis(authSub)) return { blocked: false, reason: '' };

  const status = live?.subscription?.status ?? authSub.status;
  const used   = live?.subscription?.gustavoCreditsUsedThisPeriod ?? authSub.gustavoUsed ?? 0;
  // El límite no cambia en sesión (es del plan); el used sí. Tomamos el límite del plan en vivo
  // si está, si no del auth state.
  const limit  = live?.plans?.find((p) => p.key === live?.plan?.key)?.limits?.gustavoMonthlyCredits
    ?? authSub.gustavoLimit;

  if (status === 'suspended' || status === 'canceled') {
    return { blocked: true, reason: 'Tu suscripción está suspendida — Gustavo está deshabilitado. Regularizá el pago para reactivarlo.' };
  }
  // El demo queda exento del cupo de Gustavo (usa el tope por hora).
  if (!isDemo && limit != null && used >= limit) {
    return { blocked: true, reason: `Tu organización agotó el cupo mensual de Gustavo (${limit} créditos). Subí de plan o esperá la renovación.` };
  }
  return { blocked: false, reason: '' };
}
